// @flow
import * as React from 'react';
import type { ChildrenArray } from 'react';
import {
  usePropsValidation,
  types as t,
} from '../../validation/runtime-checks/index.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/types.js';
import type { MathString } from '../../types.js';
import { mathStringChecker } from '../../types.js';
import type { SharedSubstantativeChildren } from '../common-children-types.js';
import { sharedSubstantativeChildrenChecker } from '../common-children-types.js';
import { productColor } from '../../designSystem.js';
import { InternalHeading } from '../../InternalHeading.js';
import {
  InternalSection,
  InternalDescription,
  InternalPanel,
} from '../common-components/internal-components.js';
import { INVTNContinueCondition } from './INVTNContinueCondition.js';

export function INVTNReflect(props: {|
  children: React$Element<typeof INVTNDiscussionSection>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNReflect);
  if (failedValidation) return null;

  const { children } = props;

  return (
    <div style={{ color: productColor.shuttleGrey, marginBottom: 40 }}>
      <InternalHeading level={2}>Reflect</InternalHeading>
      {children}
    </div>
  );
}
INVTNReflect.spec = {
  children: t.elementType(INVTNDiscussionSection),
};

export function INVTNDiscussionSection(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVTNDiscussionDescription>
    | React$Element<typeof INVTNDiscussionPanel>
    | React$Element<typeof INVTNContinueCondition>,
  >,
  timeEstimate?: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNDiscussionSection,
  );
  if (failedValidation) return null;

  const { children, timeEstimate } = props;

  return (
    <InternalSection
      title={timeEstimate ? `Discuss (${timeEstimate} mins)` : 'Discuss'}
    >
      {children}
    </InternalSection>
  );
}
INVTNDiscussionSection.spec = {
  children: childrenArrayMatcher(
    { min: 1, max: 1, elementType: t.elementType(INVTNDiscussionDescription) },
    { min: 1, max: 1, elementType: t.elementType(INVTNDiscussionPanel) },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVTNContinueCondition),
    },
  ),
  timeEstimate: t.optional(t.number),
};

export function INVTNDiscussionDescription(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNDiscussionDescription,
  );
  if (failedValidation) return null;

  return <InternalDescription>{props.children}</InternalDescription>;
}
INVTNDiscussionDescription.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVTNDiscussionPanel(props: {|
  children: ChildrenArray<SharedSubstantativeChildren>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNDiscussionPanel);
  if (failedValidation) return null;

  return (
    <InternalPanel heading="Discussion Guide">{props.children}</InternalPanel>
  );
}
INVTNDiscussionPanel.spec = {
  children: t.childrenArray(sharedSubstantativeChildrenChecker),
};
